// R4 (hook) — interpolated scroll sync. One pane leads; the other follows by
// piecewise-linear interpolation between anchor pairs, keeping the reading line
// (~24% down the pane) aligned with no inserted whitespace, with the document
// boundaries pinned so the leader's top/bottom maps to the follower's
// top/bottom exactly (`followerScrollTop`). The leader is derived from WHERE the
// scroll event originates — the container the reader actually scrolled — with
// the programmatic follower scroll suppressed via an "expected scrollTop" ref.
// Origin detection (not hover) is primary, so it works on touch, which never
// fires hover.
//
// This is the reader's OPT-IN mode, not its default: `reading-view.tsx` resolves
// the mode as `getPref("syncMode", "hard")`, so interpolation runs only when the
// reader asks for it via the toolbar toggle or `?sync=interpolated` — the param
// marks the exception, not the rule (SPEC "Sync anchors").
//
// This is a legitimate effect: it synchronizes with external browser systems
// (scroll events, ResizeObserver, font loading). All listeners/observers are
// cleaned up; the segments live in a ref because the scroll handler reads them
// imperatively and no render depends on them (react-useeffect-discipline).

import { useEffect, useRef, type RefObject } from "react";
import type { CorpusAnchorPair } from "@bindery/shared/corpus/anchors";
import type { DocumentModel } from "@bindery/shared/corpus/document";
import type { PaneId } from "@bindery/shared/corpus/reader";
import {
  buildSyncSegments,
  followerScrollTop,
  type PaneScrollView,
  type SyncSegmentList,
} from "./anchor-geometry";
import { measurePane } from "./pane-measure";

/** Reading line as a fraction of pane height (SPEC: ~20–28%). */
const TRIGGER_RATIO = 0.24;
/** Tolerance (px) for recognizing our own programmatic follower scroll. */
const ECHO_EPSILON = 1.5;

interface UseSyncScrollArgs {
  retellingRef: RefObject<HTMLDivElement | null>;
  originalRef: RefObject<HTMLDivElement | null>;
  retellingModel: DocumentModel;
  originalModel: DocumentModel | null;
  anchors: readonly CorpusAnchorPair[];
  /** Both panes visible AND interpolated mode. */
  enabled: boolean;
  /** Per-pane font sizes; a change relayouts the panes, so we remeasure. */
  retellingFontSize: number;
  originalFontSize: number;
  /** Opaque token; changing it re-runs the effect (e.g. the original pane
   *  remounting across the responsive breakpoint, so refs are re-read). */
  layoutKey: string;
}

export function useSyncScroll({
  retellingRef,
  originalRef,
  retellingModel,
  originalModel,
  anchors,
  enabled,
  retellingFontSize,
  originalFontSize,
  layoutKey,
}: UseSyncScrollArgs): void {
  const segmentsRef = useRef<SyncSegmentList>([]);
  const expectedRef = useRef(new Map<HTMLElement, number>());

  useEffect(() => {
    const retelling = retellingRef.current;
    const original = originalRef.current;
    if (!enabled || !retelling || !original || !originalModel) return;

    // Stable across the effect's life (useRef identity never changes); copied to
    // a local so the cleanup closes over it directly (react-hooks lint).
    const expectedTops = expectedRef.current;

    const paneOf = (el: HTMLElement): PaneId =>
      el === retelling ? "retelling" : "original";
    const followerEl = (el: HTMLElement): HTMLElement =>
      el === retelling ? original : retelling;
    const paneView = (el: HTMLElement): PaneScrollView => ({
      readingLine: el.clientHeight * TRIGGER_RATIO,
      maxScrollTop: Math.max(0, el.scrollHeight - el.clientHeight),
    });

    const remeasure = (): void => {
      segmentsRef.current = buildSyncSegments(
        anchors,
        measurePane(retelling, retellingModel),
        measurePane(original, originalModel),
      );
    };

    const syncFrom = (leaderEl: HTMLElement): void => {
      const segments = segmentsRef.current;
      if (segments.length === 0) return;
      const leader = paneOf(leaderEl);
      const follower = leader === "retelling" ? "original" : "retelling";
      const target = followerEl(leaderEl);
      // Scroll-space interpolation: interior anchors align at the reading
      // line; the endpoints pin the panes' scroll ranges to each other, so
      // the leader reaching its very top/bottom always brings the follower
      // to its own (already clamped to [0, follower max]).
      const next = followerScrollTop(
        segments,
        leader,
        follower,
        leaderEl.scrollTop,
        { retelling: paneView(retelling), original: paneView(original) },
      );
      // Record the value we're about to set so its echoed scroll event is
      // recognized as ours and doesn't flip the leader back. `behavior:
      // "instant"` is required, not cosmetic: an explicit behavior overrides
      // the container's CSS `scroll-behavior`, so this emits exactly one
      // scroll event at `next` — a `scrollTop` assignment under CSS smooth
      // scrolling animates instead, firing a stream of intermediate events
      // that the ±1.5px echo check can't recognize, flipping leadership back
      // and forth between the panes. Skip recording when the target is
      // already at `next`: the assignment is a no-op, so no echo will ever
      // arrive to clear the entry, and a stale one would swallow the
      // follower's next genuine gesture event that happens to land nearby.
      if (Math.abs(target.scrollTop - next) > ECHO_EPSILON) {
        expectedTops.set(target, next);
      }
      target.scrollTo({ top: next, behavior: "instant" });
    };

    let frame = 0;
    let pendingLeader: HTMLElement | null = null;
    const onScroll = (ev: Event): void => {
      const el = ev.currentTarget as HTMLElement;
      const expected = expectedTops.get(el);
      if (
        expected !== undefined &&
        Math.abs(el.scrollTop - expected) <= ECHO_EPSILON
      ) {
        expectedTops.delete(el);
        return; // our own programmatic follower scroll — ignore.
      }
      expectedTops.delete(el);
      pendingLeader = el;
      if (frame) return;
      frame = requestAnimationFrame(() => {
        frame = 0;
        if (pendingLeader) syncFrom(pendingLeader);
        pendingLeader = null;
      });
    };

    retelling.addEventListener("scroll", onScroll, { passive: true });
    original.addEventListener("scroll", onScroll, { passive: true });

    const observer = new ResizeObserver(() => {
      remeasure();
    });
    observer.observe(retelling);
    observer.observe(original);

    remeasure();
    // Fonts shift layout; rebuild once they settle, then align the original to
    // the retelling's current reading line so the pane opens in sync.
    document.fonts?.ready
      .then(() => {
        remeasure();
        syncFrom(retelling);
      })
      .catch(() => undefined);
    syncFrom(retelling);

    return () => {
      retelling.removeEventListener("scroll", onScroll);
      original.removeEventListener("scroll", onScroll);
      observer.disconnect();
      if (frame) cancelAnimationFrame(frame);
      expectedTops.clear();
    };
  }, [
    retellingRef,
    originalRef,
    retellingModel,
    originalModel,
    anchors,
    enabled,
    retellingFontSize,
    originalFontSize,
    layoutKey,
  ]);
}
